import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import { getData } from "country-list";
import { Eye, EyeOff } from "lucide-react";
import {
  useRegisterStudentMutation,
  useVerifyEmailAddressMutation,
} from "../services/auth/authApi";
import Button from "../components/common/Button";
import FormInput from "../components/common/FormInput";
import { markSignupVerified } from "../store/signupStorage";
import "@fontsource/work-sans/400.css";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import {
  EmergencyContact,
  LockIcon,
  OrangeProfile,
  OrangeProfileFilled,
  ProfileLocationIcon,
  Upload,
  WhiteProfile,
} from "../components/Icons";
import PhoneInput from "react-phone-input-2";
import { AuthNavBar } from "../components/Navbar";
import { useGetAllAgeGroupsQuery } from "../services/category/categoryApi";

const phoneInputClassNames = {
  containerClass: "!w-full",
  inputClass:
    "!w-full !h-[46px] !rounded-lg !border !border-[#F9881763] !bg-transparent !text-base !leading-6 !pl-12 !text-black placeholder:!font-light placeholder:!text-[#8C8CA1] focus:!border-[#FF6636] focus:!outline-none",
  buttonClass:
    "!h-[46px] !rounded-l-lg !border !border-[#F9881763] !border-r-0 !bg-transparent",
  dropdownClass: "!bg-white",
};

const StudentRegistration = () => {
  const navigate = useNavigate();
  const [
    registerStudent,
    { isLoading: isRegisteringStudent, error: registerStudentError },
  ] = useRegisterStudentMutation();

  const countries = getData();
  const [verifyEmailAddress, { isLoading: isVerifyingOtp }] =
    useVerifyEmailAddressMutation();

  const { data, isLoading, error } = useGetAllAgeGroupsQuery();

  const ageGroups = data?.data || [];
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImageError, setProfileImageError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpInfoMessage, setOtpInfoMessage] = useState("");
  const [verifiedMessage, setVerifiedMessage] = useState(
    "Successfully Verified",
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const otpInputsRef = useRef([]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      phoneNumber: "",
      password: "",
      confirmSecPassword: "",
      agreeTerms: false,
    },
  });

  const password = watch("password");
  const confirmSecPassword = watch("confirmSecPassword");
  const email = watch("email");

  React.useEffect(() => {
    if (confirmSecPassword) {
      trigger(["password", "confirmSecPassword"]);
    }
  }, [password, confirmSecPassword, trigger]);

  React.useEffect(() => {
    return () => {
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  const onSubmit = async (data) => {
    if (!profileImageFile) {
      setProfileImageError("Profile picture is required");
      return;
    }

    setProfileImageError("");

    try {
      const submittedData = { ...data };
      delete submittedData.confirmSecPassword;
      delete submittedData.agreeTerms;

      if (typeof submittedData.bio === "string") {
        submittedData.bio = submittedData.bio.trim();
      }

      const studentPayload = new FormData();

      Object.entries(submittedData).forEach(([key, value]) => {
        studentPayload.append(key, value ?? "");
      });

      if (profileImageFile) {
        studentPayload.append("profilePicture", profileImageFile);
      }

      studentPayload.append(
        "consentAccepted",
        String(Boolean(data.agreeTerms)),
      );
      await registerStudent(studentPayload).unwrap();
      setOtpValues(["", "", "", ""]);
      setOtpError("");
      setOtpInfoMessage("");
      setShowVerifiedModal(false);
      setShowOtpModal(true);
    } catch (error) {
      console.error("Student profile submission failed", error);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextValues = [...otpValues];
    nextValues[index] = digit;
    setOtpValues(nextValues);
    setOtpError("");

    if (digit && index < 3) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otpValues.join("");
    if (!/^\d{4}$/.test(otpCode)) {
      setOtpError("Please enter a valid 4-digit OTP");
      return;
    }

    if (!email) {
      setOtpError("Email is missing. Please refresh and try again.");
      return;
    }

    try {
      setOtpError("");
      setOtpInfoMessage("");

      const response = await verifyEmailAddress({
        email,
        otp: otpCode,
      }).unwrap();

      setVerifiedMessage(response?.message || "Successfully Verified");
      markSignupVerified();
      setShowOtpModal(false);
      setShowVerifiedModal(true);
    } catch (error) {
      const backendMessage =
        error?.data?.message || error?.message || "OTP verification failed";
      setOtpError(backendMessage);
    }
  };

 

  const handleVerifiedContinue = () => {
    navigate("/login", { replace: true });
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImageError("");
      const imageUrl = URL.createObjectURL(file);

      setProfileImage((previousImage) => {
        if (previousImage) {
          URL.revokeObjectURL(previousImage);
        }
        return imageUrl;
      });
      setProfileImageFile(file);
    }
  };

  return (
    <div>
      <AuthNavBar />
      <div className="min-h-screen bg-[#EFEFFD] px-4 pt-6 pb-12 sm:px-6 sm:pt-9 sm:pb-16 lg:px-8">
        <div className="mb-5 text-center tracking-[-2%] sm:mb-[26px]">
          <h1 className="font-['Work_Sans'] text-2xl font-semibold leading-tight text-black sm:text-3xl lg:text-[34px] lg:leading-10">
            Join Our Learning Platform
          </h1>
          <p className="mt-2 text-base leading-6 text-[#636363] sm:mt-0 sm:text-lg sm:leading-[26px] lg:text-[22px]">
            Create your account to get started
          </p>
        </div>
        <div className="mx-auto w-full max-w-[1004px]">
          <div className="rounded-[18px] bg-[#F5F5F5] px-4 pt-4 pb-6 shadow-[0_2px_5.7px_0_#00000026] sm:px-6 sm:pt-[22px] sm:pb-7 lg:px-[35px]">
            <div className="mb-5 rounded-xl border border-[#D3D3D3] bg-[#FF6636] px-4 py-3 text-center sm:mb-7 sm:px-6">
              <h2 className="flex items-center justify-center gap-2 text-xl font-medium text-white sm:gap-2.5 sm:text-2xl lg:text-[28px]">
                <WhiteProfile /> Student
              </h2>
            </div>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-8 lg:pt-11">
                <h3 className="flex items-center gap-2 text-lg font-medium leading-6 text-black sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <OrangeProfileFilled /> Personal Information
                </h3>

                <label className="block mb-8 sm:mb-14 text-center cursor-pointer">
                  <div
                    className={`relative mx-auto mb-[18px] mt-2 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-[#FF663614] ${
                      profileImageError ? "ring-2 ring-[#C62828]" : ""
                    }`}
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <OrangeProfile />
                    )}

                    <div className="absolute bottom-0 right-0">
                      <div className="bg-[#FF6636] flex items-center justify-center rounded-full w-[33px] h-[33px] ">
                        <Upload />
                      </div>
                    </div>
                  </div>

                  <p className="text-base font-light leading-5 text-black sm:text-xl">
                    Upload Profile Picture*
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="hidden"
                  />
                </label>
                {profileImageError ? (
                  <p className="-mt-6 mb-6 text-center text-sm font-medium text-[#C62828] sm:-mt-10 sm:mb-10">
                    {profileImageError}
                  </p>
                ) : null}

                <div className="grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="firstName"
                    label="First Name*"
                    type="text"
                    placeholder="Enter First Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.firstName?.message}
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                  />

                  <FormInput
                    id="lastName"
                    label="Last Name*"
                    type="text"
                    placeholder="Enter Last Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.lastName?.message}
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                  />

                  <FormInput
                    id="fatherName"
                    label="Father Name*"
                    type="text"
                    placeholder="Enter Father's Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.fatherName?.message}
                    {...register("fatherName", {
                      required: "Father's name is required",
                    })}
                  />
                </div>

                <div className="mt-7 grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="email"
                    label="Email*"
                    type="email"
                    placeholder="Enter email"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.email?.message}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email",
                      },
                    })}
                  />

                  <div className="relative w-full">
                    <label
                      htmlFor="phoneNumber"
                      className="mb-1.5 block text-base leading-5 text-black sm:text-lg"
                    >
                      Phone No*
                    </label>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country="pk"
                          placeholder="Enter phone number"
                          enableSearch
                          value={value || ""}
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "phoneNumber",
                            name: "phoneNumber",
                          }}
                          {...phoneInputClassNames}
                        />
                      )}
                    />
                    {errors.phoneNumber ? (
                      <p className="mt-2 text-sm text-[#C62828]">
                        {errors.phoneNumber.message}
                      </p>
                    ) : null}
                  </div>
                  <div className="relative w-full h-full">
                    <label
                      htmlFor="parentPhoneNumber"
                      className="mb-1.5 block text-base leading-5 text-black sm:text-lg"
                    >
                      Parent's Phone No*
                    </label>
                    <Controller
                      name="parentPhoneNumber"
                      control={control}
                      rules={{
                        required: "Parent's phone is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Parent's phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country="pk"
                          enableSearch
                          value={value || ""}
                          placeholder="Enter parent's phone number"
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "parentPhoneNumber",
                            name: "parentPhoneNumber",
                          }}
                          {...phoneInputClassNames}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="mt-7 grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="age"
                    label="Age*"
                    type="text"
                    placeholder="Enter age"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.age?.message}
                    {...register("age", {
                      required: "Age is required",
                    })}
                  />

                  <div>
                    <label className="block text-base leading-5 text-black sm:text-lg">
                      Age Group*
                    </label>
                    <select
                      className="px-5 mt-1.5  h-[46px] w-full rounded-lg border border-[#F9881763] bg-transparent text-black    focus:border-[#FF6636] focus:outline-none appearance-none"
                      {...register("ageGroup", {
                        required: "Age group is required",
                      })}
                    >
                      <option value="" disabled>
                        Select Age Group
                      </option>
                      {ageGroups.map((ageGroup) => (
                        <option key={ageGroup._id} value={ageGroup._id}>
                          {ageGroup.title}
                        </option>
                      ))}
                    </select>
                    {errors.ageGroup && (
                      <p className="text-sm text-[#C62828]">
                        {errors.ageGroup.message}
                      </p>
                    )}
                  </div>

                  <FormInput
                    id="dateOfBirth"
                    label="Date of Birth*"
                    type="date"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.dateOfBirth?.message}
                    {...register("dateOfBirth", {
                      required: "Date of birth is required",
                    })}
                  />
                </div>
                <div className="mt-7">
                  <label className="block text-base leading-5 text-black sm:text-lg">
                    Bio*
                  </label>
                  <textarea
                    placeholder="Type here..."
                    maxLength={100}
                    className="px-5 mt-1.5 min-h-32 w-full rounded-lg border border-[#F9881763] bg-transparent py-3  placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none resize-none"
                    {...register("bio", {
                      validate: (value) => {
                        const trimmed = (value ?? "").trim();
                        if (!trimmed) return "Bio is required";
                        if (trimmed.length < 20 || trimmed.length > 100) {
                          return "Bio must be 20–100 characters";
                        }
                        return true;
                      },
                      onBlur: (event) => {
                        const trimmed = event.target.value.trim();
                        if (trimmed !== event.target.value) {
                          setValue("bio", trimmed, { shouldValidate: true });
                        }
                      },
                    })}
                  />
                  {errors.bio && (
                    <p className="text-sm text-[#C62828]">
                      {errors.bio.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-9 lg:pt-11">
                <h3 className="mb-6 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-9 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <ProfileLocationIcon /> Residence Information
                </h3>

                <FormInput
                  id="address"
                  label="Address*"
                  type="text"
                  placeholder="Street address"
                  labelClassName="block text-base text-black sm:text-lg"
                  inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                  errorMessage={errors.address?.message}
                  {...register("address", { required: "Address is required" })}
                />

                <div className="mt-7 grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="city"
                    label="City*"
                    type="text"
                    placeholder="Enter City Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.city?.message}
                    {...register("city", { required: "City is required" })}
                  />

                  <div>
                    <label className="block text-base leading-5 text-black sm:text-lg">
                      Country*
                    </label>
                    <select
                      className="px-5 mt-1.5 h-[46px] w-full rounded-lg border border-[#F9881763] bg-transparent   focus:border-[#FF6636] focus:outline-none appearance-none"
                      {...register("country", {
                        required: "Country is required",
                      })}
                    >
                      <option value="" disabled>
                        Select Country
                      </option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <p className="text-sm text-[#C62828]">
                        {errors.country.message}
                      </p>
                    )}
                  </div>

                  <FormInput
                    id="postalCode"
                    label="Postal Code*"
                    type="text"
                    placeholder="12345"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.postalCode?.message}
                    {...register("postalCode", {
                      required: "Postal code is required",
                    })}
                  />
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-9 lg:pt-11">
                <h3 className="mb-6 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-9 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <EmergencyContact /> Emergency Contacts
                </h3>

                <div className="grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="fullName"
                    label="Full Name*"
                    type="text"
                    placeholder="Enter Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.fullName?.message}
                    {...register("fullName", {
                      required: "Full name is required",
                    })}
                  />

                  <div>
                    <label className="block text-base leading-5 text-black sm:text-lg">
                      Relationship*
                    </label>
                    <select
                      className="px-5 mt-1.5 h-[46px] w-full rounded-lg border border-[#F9881763] bg-transparent  focus:border-[#FF6636] focus:outline-none appearance-none"
                      {...register("relationship", {
                        required: "Relationship is required",
                      })}
                    >
                      <option value="">Select relationship</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="grandparent">Grandparent</option>
                      <option value="guardian">Guardian</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.relationship && (
                      <p className="text-sm text-[#C62828]">
                        {errors.relationship.message}
                      </p>
                    )}
                  </div>

                  <div className="relative w-full h-full">
                    <label
                      htmlFor="emergencyPhoneNumber"
                      className="mb-1.5 block text-base leading-5 text-black sm:text-lg"
                    >
                      Phone No*
                    </label>
                    <Controller
                      name="emergencyPhoneNumber"
                      control={control}
                      rules={{
                        required: "Phone is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country="pk"
                          enableSearch
                          placeholder="Enter emergency phone number"
                          value={value || ""}
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "emergencyPhoneNumber",
                            name: "emergencyPhoneNumber",
                          }}
                          {...phoneInputClassNames}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-10 lg:pt-11">
                <h3 className="mb-6 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-9 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <LockIcon /> Account Security
                </h3>

                <div className="grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2">
                  <FormInput
                    id="password"
                    label="Password*"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 pr-12 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.password?.message}
                    rightContent={
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className="pointer-events-auto text-[#8C8CA1] hover:text-[#FF6636]"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                    {...register("password", {
                      required: "Password is required",
                      validate: {
                        length: (value) =>
                          /^.{8,15}$/.test(value) ||
                          "Password must be 8-15 characters",
                        letter: (value) =>
                          /[A-Za-z]/.test(value) ||
                          "Password must contain at least one letter",
                        number: (value) =>
                          /\d/.test(value) ||
                          "Password must contain at least one number",
                        special: (value) =>
                          /[^A-Za-z0-9]/.test(value) ||
                          "Password must contain at least one special character",
                        match: (value) => {
                          const confirm = getValues("confirmSecPassword");
                          if (!confirm) return true;
                          return value === confirm || "Passwords do not match";
                        },
                      },
                    })}
                  />

                  <FormInput
                    id="confirmSecPassword"
                    label="Confirm Password*"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter password"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 pr-12 mt-1.5 h-[46px] rounded-lg border border-[#F9881763] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#FF6636] focus:outline-none"
                    errorMessage={errors.confirmSecPassword?.message}
                    rightContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword((current) => !current)
                        }
                        className="pointer-events-auto text-[#8C8CA1] hover:text-[#FF6636]"
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    }
                    {...register("confirmSecPassword", {
                      required: "Please confirm your password",
                      validate: (value) => {
                        const currentPassword = getValues("password");
                        return (
                          value === currentPassword || "Passwords do not match"
                        );
                      },
                    })}
                  />
                </div>
              </div>

              <label className="mt-6 flex items-start gap-2.5 sm:mt-8">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#FF6636]"
                  {...register("agreeTerms", {
                    validate: (value) =>
                      value || "Please accept Terms & Conditions",
                  })}
                />
                <span className="font-['Work_Sans'] text-sm leading-[21px] text-black">
                  I agree to the Terms & Conditions and Privacy Policy.
                </span>
              </label>
              {errors.agreeTerms ? (
                <p className="mt-2 font-['Work_Sans'] text-sm font-medium text-[#C62828]">
                  {errors.agreeTerms.message}
                </p>
              ) : null}

              <Button
                type="submit"
                disabled={isSubmitting || isRegisteringStudent}
                btnText={
                  isSubmitting || isRegisteringStudent
                    ? "Submitting..."
                    : "Sign Up as Student"
                }
                btnClassName="mt-6 w-full rounded-lg bg-[#FF6636] px-6 py-2.5 text-lg font-medium text-white transition-colors hover:bg-[#E85A2A] disabled:opacity-70 sm:mt-8 sm:text-xl"
              />
            </form>

            {registerStudentError ? (
              <p className="mt-4 text-sm font-medium text-[#C62828]">
                {registerStudentError?.data?.message ||
                  "Student registration failed. Please try again."}
              </p>
            ) : null}

            <p className="mt-5 text-center text-base text-black sm:mt-6 sm:text-lg">
              Already have an account?{" "}
              {isSubmitting || isRegisteringStudent ? (
                <span className="cursor-not-allowed font-medium text-[#FF6636] opacity-50">
                  Login here
                </span>
              ) : (
                <Link to="/login" className="font-medium text-[#FF6636]">
                  Login here
                </Link>
              )}
            </p>
          </div>
        </div>
      </div>

      {showOtpModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[420px] rounded-[18px] bg-[#F5F5F5] px-5 pb-10 pt-8 font-['Work_Sans'] sm:px-8 sm:pb-14 sm:pt-12 lg:px-12">
            <h2 className="text-center text-xl font-semibold leading-7 tracking-[-2%] text-black sm:text-2xl">
              Enter OTP
            </h2>
            <p className="mt-3 text-center font-medium text-sm leading-4 text-black">
              We have sent a OTP to your Email
            </p>
            <p className="mt-3 text-xs  leading-5 text-black">
              Enter 4-digit OTP
            </p>

            <div className=" flex items-center justify-center gap-5 sm:gap-8 ">
              {otpValues.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    otpInputsRef.current[index] = element;
                  }}
                  type="text"
                  value={digit}
                  onChange={(event) =>
                    handleOtpChange(index, event.target.value)
                  }
                  onKeyDown={(event) => handleOtpKeyDown(index, event)}
                  inputMode="numeric"
                  maxLength={1}
                  className="h-11 w-12 rounded-lg border-[0.5px] border-[#FF6636] bg-[#FAFCFE] text-center text-xl font-semibold text-[#131A2C] focus:border-[#FF6636] focus:outline-none sm:h-12 sm:w-14 sm:text-2xl"
                />
              ))}
            </div>

            {otpError ? (
              <p className="mt-4 text-center text-sm font-medium text-[#C62828]">
                {otpError}
              </p>
            ) : null}

            {otpInfoMessage ? (
              <p className="mt-4 text-center text-sm font-medium text-[#2E7D32]">
                {otpInfoMessage}
              </p>
            ) : null}

            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={isVerifyingOtp}
              className="mt-3 h-9 w-full rounded-full bg-[#FF6636]  font-medium text-white tracking-[4%]"
            >
              {isVerifyingOtp ? "Verifying..." : "Verify"}
            </button>

           

            <p className="mt-2 text-center text-xs text-[#98A2B3]">
              {email ? `OTP for: ${email}` : ""}
            </p>
          </div>
        </div>
      ) : null}

      {showVerifiedModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="flex min-h-[280px] w-full max-w-[420px] flex-col items-center justify-center rounded-[18px] bg-[#F5F5F5] px-6 py-10 sm:h-[360px] sm:py-14">
            <h2 className="text-center text-xl font-semibold tracking-[-2%] text-black sm:text-2xl">
              {verifiedMessage}
            </h2>

            <button
              type="button"
              onClick={handleVerifiedContinue}
              className=" mt-8 block h-8 w-full max-w-[255px] rounded-full bg-[#FF6636] leading-5 font-medium text-white"
            >
              Go To Login
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StudentRegistration;
